/**
 * Native app login page - PKCE flow for desktop and mobile apps.
 *
 * Flow:
 * 1. Native app opens this page with PKCE challenge
 * 2. User authenticates via email/password or existing session
 * 3. Web app calls API to create authorization code
 * 4. Web app redirects to native app with authorization code
 * 5. Native app exchanges code for session token
 */

import * as stylex from "@stylexjs/stylex";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { ClientType, isValidRedirectUri } from "@animus/utils";

import { AuthApi } from "../../auth/auth-api-client.ts";
import { ENVIRONMENT } from "../../environment";
import {
  alert,
  button,
  card,
  form,
  layout,
  page,
  spacing,
  spinner,
  text,
} from "../../styles.stylex";

type NativeLoginStatus =
  | "checking"
  | "need_login"
  | "logging_in"
  | "authorizing"
  | "success"
  | "error";

type NativeClientType = typeof ClientType.Desktop | typeof ClientType.Mobile;

interface NativeLoginSearch {
  redirect_uri?: string;
  code_challenge?: string;
  code_challenge_method?: string;
  client_type?: string;
  state?: string;
}

function isNativeClientType(
  type: string | undefined
): type is NativeClientType {
  return type === ClientType.Desktop || type === ClientType.Mobile;
}

interface AuthorizeResponse {
  code: string;
  expiresIn: number;
}

async function createAuthorizationCode(
  codeChallenge: string,
  codeChallengeMethod: string,
  redirectUri: string,
  clientType: ClientType
): Promise<AuthorizeResponse> {
  const response = await fetch(
    `${ENVIRONMENT.API_BASE_URL}/rpc/auth/native/authorize`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod,
        redirect_uri: redirectUri,
        client_type: clientType,
      }),
    }
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      (errorBody as { error?: { message?: string } }).error?.message ||
        "Failed to create authorization code"
    );
  }

  return response.json() as Promise<AuthorizeResponse>;
}

export function NativeLoginPage() {
  const search = useSearch({ strict: false }) as NativeLoginSearch;
  const navigate = useNavigate();
  const [status, setStatus] = useState<NativeLoginStatus>("checking");
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null);

  const redirectUri = search.redirect_uri ?? "";
  const codeChallenge = search.code_challenge ?? "";
  const codeChallengeMethod = search.code_challenge_method ?? "S256";
  const clientTypeParam = search.client_type ?? "desktop";
  const stateParam = search.state;

  const clientType: ClientType = isNativeClientType(clientTypeParam)
    ? clientTypeParam
    : ClientType.Desktop;

  const handleAuthorize = useCallback(async () => {
    setStatus("authorizing");

    try {
      const result = await createAuthorizationCode(
        codeChallenge,
        codeChallengeMethod,
        redirectUri,
        clientType
      );

      const url = new URL(redirectUri);
      url.searchParams.set("code", result.code);
      if (stateParam) {
        url.searchParams.set("state", stateParam);
      }
      setCallbackUrl(url.toString());
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Authorization failed");
    }
  }, [codeChallenge, codeChallengeMethod, redirectUri, clientType, stateParam]);

  const allowDevLocalhost = ENVIRONMENT.DEPLOY_ENVIRONMENT === "development";

  useEffect(() => {
    if (!isNativeClientType(clientTypeParam)) {
      setStatus("error");
      setError("Invalid client_type");
      return;
    }
    if (!isValidRedirectUri(redirectUri, clientType, allowDevLocalhost)) {
      setStatus("error");
      setError(`Invalid redirect_uri for ${clientType}`);
      return;
    }
    if (!codeChallenge) {
      setStatus("error");
      setError("Missing PKCE code challenge");
      return;
    }
    if (codeChallengeMethod !== "S256") {
      setStatus("error");
      setError("Only S256 code_challenge_method is supported");
      return;
    }

    AuthApi.getSession().then((session) => {
      if (session && !session.user.isAnonymous) {
        handleAuthorize();
      } else {
        setStatus("need_login");
      }
    });
  }, [
    redirectUri,
    codeChallenge,
    codeChallengeMethod,
    clientType,
    clientTypeParam,
    allowDevLocalhost,
    handleAuthorize,
  ]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("logging_in");
    setError(null);

    const result = await AuthApi.signIn(email, password);

    if (result.isErr) {
      setStatus("need_login");
      setError(result.error.message ?? "Login failed");
      return;
    }

    await handleAuthorize();
  }

  const appName = clientType === ClientType.Desktop ? "Desktop" : "Mobile";

  if (status === "checking" || status === "authorizing") {
    return (
      <div {...stylex.props(layout.centered, page.dark)}>
        <div {...stylex.props(spinner.base)} />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div {...stylex.props(layout.centered, page.dark)}>
        <div {...stylex.props(card.base, text.center)}>
          <h1
            {...stylex.props(spacing.mb4, text.bold, text["2xl"], text.white)}
          >
            Error
          </h1>
          <p {...stylex.props(spacing.mb6, text.error)}>{error}</p>
          <button
            {...stylex.props(button.base, button.ghost)}
            onClick={() => navigate({ to: "/" })}
            type="button"
          >
            Return home
          </button>
        </div>
      </div>
    );
  }

  if (status === "success" && callbackUrl) {
    return (
      <div {...stylex.props(layout.centered, page.dark)}>
        <div {...stylex.props(card.base, text.center)}>
          <div {...stylex.props(spacing.mb4, text.success)}>
            Login successful!
          </div>
          <p {...stylex.props(spacing.mb6, text.muted)}>
            Click below to return to the {appName.toLowerCase()} app.
          </p>
          <a
            {...stylex.props(button.base, button.primary, button.full)}
            href={callbackUrl}
          >
            Open Animus {appName}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div {...stylex.props(layout.centered, page.dark)}>
      <div {...stylex.props(card.base)}>
        <h1
          {...stylex.props(
            spacing.mb2,
            text.center,
            text.bold,
            text["2xl"],
            text.white
          )}
        >
          Sign in to Animus {appName}
        </h1>
        <p {...stylex.props(spacing.mb6, text.center, text.muted)}>
          Enter your credentials to authorize the {appName.toLowerCase()} app.
        </p>

        {error && <div {...stylex.props(alert.error)}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div {...stylex.props(form.group)}>
            <label {...stylex.props(form.label)} htmlFor="email">
              Email
            </label>
            <input
              {...stylex.props(form.input)}
              autoComplete="email"
              disabled={status === "logging_in"}
              id="email"
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              value={email}
            />
          </div>

          <div {...stylex.props(form.groupLast)}>
            <label {...stylex.props(form.label)} htmlFor="password">
              Password
            </label>
            <input
              {...stylex.props(form.input)}
              autoComplete="current-password"
              disabled={status === "logging_in"}
              id="password"
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              value={password}
            />
          </div>

          <button
            {...stylex.props(button.base, button.primary, button.full)}
            disabled={status === "logging_in"}
            type="submit"
          >
            {status === "logging_in" ? "Signing in..." : "Sign in"}
          </button>
        </form>

        <p {...stylex.props(spacing.mt6, text.center, text.xs, text.muted)}>
          This will authorize the Animus {appName.toLowerCase()} application to
          access your account.
        </p>
      </div>
    </div>
  );
}
